import React from 'react'
import { MIN_COLUMNS, MIN_ROWS } from '../constants/guide-limits'
import { normalizeCount } from '../helpers/count-input'
import { normalizeStoredGutterPoints } from '../helpers/gutter-fit'
import { formatFieldValue } from '../helpers/gutter-formatting'
import type { ColumnsRowsSettings } from '../types/columns-rows-settings'
import type { GuideUnit } from '../types/guide-unit'

type UseGuideSettingsFormArgs = {
  open: boolean
  unit: GuideUnit
  settings: ColumnsRowsSettings
  defaultGuttersLinked: boolean
  pageInnerWidth: number
  pageInnerHeight: number
}

/**
 * The dialog's field text, gutter-link and apply state. Fields reset from `settings` each time the
 * dialog opens (not while it stays open), and any edit clears a previous apply error.
 */
export function useGuideSettingsForm({
  open,
  unit,
  settings,
  defaultGuttersLinked,
  pageInnerWidth,
  pageInnerHeight,
}: UseGuideSettingsFormArgs) {
  const wasOpenRef = React.useRef(false)
  const [isApplying, setIsApplying] = React.useState(false)
  const [applyError, setApplyError] = React.useState<string | null>(null)
  const normalizedColumnCount = normalizeCount(settings.columns, MIN_COLUMNS)
  const normalizedRowCount = normalizeCount(settings.rows, MIN_ROWS)
  const initialColGutterPoints = normalizeStoredGutterPoints(
    settings.colGutter,
    normalizedColumnCount,
    pageInnerWidth
  )
  const initialRowGutterPoints = normalizeStoredGutterPoints(
    settings.rowGutter,
    normalizedRowCount,
    pageInnerHeight
  )

  const [columnsInput, setColumnsInput] = React.useState<string>(String(normalizedColumnCount))
  const [rowsInput, setRowsInput] = React.useState<string>(String(normalizedRowCount))
  const [colGutterInput, setColGutterInput] = React.useState<string>(
    formatFieldValue(initialColGutterPoints, unit)
  )
  const [rowGutterInput, setRowGutterInput] = React.useState<string>(
    formatFieldValue(initialRowGutterPoints, unit)
  )
  const [guttersLinked, setGuttersLinked] = React.useState<boolean>(defaultGuttersLinked)

  React.useEffect(() => {
    if (!open) {
      wasOpenRef.current = false
      return
    }

    if (wasOpenRef.current) return
    wasOpenRef.current = true

    setColumnsInput(String(normalizedColumnCount))
    setRowsInput(String(normalizedRowCount))
    setColGutterInput(formatFieldValue(initialColGutterPoints, unit))
    setRowGutterInput(formatFieldValue(initialRowGutterPoints, unit))
    setGuttersLinked(defaultGuttersLinked)
    setApplyError(null)
    setIsApplying(false)
  }, [
    defaultGuttersLinked,
    initialColGutterPoints,
    initialRowGutterPoints,
    normalizedColumnCount,
    normalizedRowCount,
    open,
    unit,
  ])

  React.useEffect(() => {
    setApplyError(null)
  }, [columnsInput, rowsInput, colGutterInput, rowGutterInput])

  return {
    columnsInput,
    setColumnsInput,
    rowsInput,
    setRowsInput,
    colGutterInput,
    setColGutterInput,
    rowGutterInput,
    setRowGutterInput,
    guttersLinked,
    setGuttersLinked,
    isApplying,
    setIsApplying,
    applyError,
    setApplyError,
  }
}
